import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatMoney } from '../lib/currency';
import {
  MapPin,
  Banknote,
  Clock,
  Building2,
  Briefcase,
  Wifi,
  CheckCircle2,
  Sparkles,
  FileText,
  ArrowLeft,
  Share2,
  Bookmark,
  Loader2,
  GraduationCap,
  Award,
  Bell
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function JobDetailsPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, isJobSeeker, isAuthenticated } = useAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [generatingCoverLetter, setGeneratingCoverLetter] = useState(false);
  const [cvScore, setCvScore] = useState(null);
  const [loadingScore, setLoadingScore] = useState(false);
  const [interviewTips, setInterviewTips] = useState([]);
  const [loadingTips, setLoadingTips] = useState(false);

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  useEffect(() => {
    if (searchParams.get('apply') === 'true' && isAuthenticated && isJobSeeker) {
      setShowApplyModal(true);
    }
  }, [searchParams, isAuthenticated, isJobSeeker]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      console.error('Failed to fetch job:', error);
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=/jobs/${jobId}?apply=true`);
      return;
    }
    if (!isJobSeeker) {
      toast.error('Only job seekers can apply for jobs');
      return;
    }
    setShowApplyModal(true);
  };

  const [appliedSuccessfully, setAppliedSuccessfully] = useState(false);

  const submitApplication = async () => {
    setApplying(true);
    try {
      await axios.post(`${API_URL}/api/applications`, {
        job_id: jobId,
        cover_letter: coverLetter
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application submitted successfully!');
      setAppliedSuccessfully(true); // show alert suggestion in modal
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit application';
      toast.error(message);
    } finally {
      setApplying(false);
    }
  };

  const generateCoverLetter = async () => {
    setGeneratingCoverLetter(true);
    try {
      const response = await axios.post(`${API_URL}/api/ai/generate-cover-letter/${jobId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoverLetter(response.data.cover_letter);
      toast.success('Cover letter generated!');
    } catch (error) {
      toast.error('Failed to generate cover letter');
    } finally {
      setGeneratingCoverLetter(false);
    }
  };

  const fetchCvScore = async () => {
    if (!isAuthenticated || !isJobSeeker) return;
    setLoadingScore(true);
    try {
      const response = await axios.post(`${API_URL}/api/ai/score-cv/${jobId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCvScore(response.data);
    } catch (error) {
      console.error('Failed to fetch CV score:', error);
    } finally {
      setLoadingScore(false);
    }
  };

  const fetchInterviewTips = async () => {
    if (!isAuthenticated) return;
    setLoadingTips(true);
    try {
      const response = await axios.get(`${API_URL}/api/ai/interview-tips/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInterviewTips(response.data.tips);
    } catch (error) {
      console.error('Failed to fetch interview tips:', error);
    } finally {
      setLoadingTips(false);
    }
  };

  const formatSalary = (min, max, currency, period) => {
    if (!min && !max) return 'Competitive';
    const format = (num) => formatMoney(num, currency);
    const per = period === 'month' ? 'per month' : 'per year';
    if (min && max) return `${format(min)} - ${format(max)} ${per}`;
    if (min) return `From ${format(min)} ${per}`;
    return `Up to ${format(max)} ${per}`;
  };

  const getJobTypeLabel = (type) => {
    const labels = {
      full_time: 'Full Time',
      part_time: 'Part Time',
      contract: 'Contract',
      internship: 'Internship'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-6" />
          <Card>
            <CardContent className="p-8">
              <Skeleton className="h-10 w-2/3 mb-4" />
              <Skeleton className="h-6 w-1/3 mb-6" />
              <div className="space-y-4">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Job Not Found</h2>
            <p className="text-muted-foreground mb-4">This job may have been removed or is no longer available.</p>
            <Button onClick={() => navigate('/jobs')}>Browse All Jobs</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to jobs
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Header */}
            <Card>
              <CardContent className="p-8">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-16 h-16 rounded-xl bg-[#F0F4F8] flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-[#003366]" />
                      </div>
                      <div>
                        <h1 className="text-2xl font-bold text-foreground" data-testid="job-title">
                          {job.title}
                        </h1>
                        <p className="text-lg text-muted-foreground">{job.company_name}</p>
                        <Link to={`/employers/${job.employer_id}`} className="text-[#003366] text-sm hover:underline flex items-center gap-1"><Building2 className="w-3.5 h-3.5"/>View Company Profile & Reviews</Link>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-6">
                      <Badge variant="secondary" className="text-sm">
                        {getJobTypeLabel(job.job_type)}
                      </Badge>
                      {job.remote && (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          <Wifi className="w-3 h-3 mr-1" />
                          Remote
                        </Badge>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Banknote className="w-4 h-4" />
                        <span>{formatSalary(job.salary_min, job.salary_max, job.currency, job.salary_period)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>Posted {new Date(job.created_at).toLocaleDateString()}</span>
                      </div>
                      {job.experience_required && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <GraduationCap className="w-4 h-4" />
                          <span>{job.experience_required}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" data-testid="share-job">
                      <Share2 className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" data-testid="save-job">
                      <Bookmark className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Apply Button */}
                <div className="mt-6 pt-6 border-t flex gap-3">
                  <Button 
                    onClick={handleApply}
                    className="flex-1 bg-[#E11D48] hover:bg-[#E11D48]/90 h-12 text-base"
                    data-testid="apply-button"
                  >
                    Apply Now
                  </Button>
                  {isAuthenticated && isJobSeeker && (
                    <Button 
                      variant="outline" 
                      onClick={fetchCvScore}
                      disabled={loadingScore}
                      className="h-12"
                      data-testid="score-cv-button"
                    >
                      {loadingScore ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                          Score My CV
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* CV Score Result */}
            {cvScore && (
              <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    AI CV Score
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div className="text-center p-4 bg-white rounded-xl">
                      <div className="text-4xl font-bold text-[#003366]">{cvScore.score}%</div>
                      <div className="text-sm text-muted-foreground mt-1">Overall Score</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-xl">
                      <div className="text-4xl font-bold text-purple-600">{cvScore.match_percentage}%</div>
                      <div className="text-sm text-muted-foreground mt-1">Skills Match</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium text-emerald-700 mb-2">Strengths</h4>
                      <ul className="space-y-1">
                        {cvScore.strengths.map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-amber-700 mb-2">Areas to Improve</h4>
                      <ul className="space-y-1">
                        {cvScore.improvements.map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <Award className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Job Description */}
            <Card>
              <CardHeader>
                <CardTitle>Job Description</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none" data-testid="job-description">
                  <p className="whitespace-pre-wrap">{job.description}</p>
                </div>
              </CardContent>
            </Card>

            {/* Requirements */}
            {job.requirements?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Requirements</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {job.requirements.map((req, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#003366] mt-1 flex-shrink-0" />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Benefits */}
            {job.benefits?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Benefits</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {job.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Skills */}
            {job.skills_required?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Skills Required</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {job.skills_required.map((skill, idx) => (
                      <Badge key={idx} variant="secondary">{skill}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Interview Tips */}
            {isAuthenticated && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                      Interview Tips
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {interviewTips.length > 0 ? (
                    <ul className="space-y-3">
                      {interviewTips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0 text-xs font-medium">
                            {idx + 1}
                          </span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Button 
                      variant="outline" 
                      onClick={fetchInterviewTips}
                      disabled={loadingTips}
                      className="w-full"
                      data-testid="get-interview-tips"
                    >
                      {loadingTips ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                      )}
                      Get AI Interview Tips
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Apply Modal */}
      <Dialog open={showApplyModal} onOpenChange={v => { setShowApplyModal(v); if (!v && appliedSuccessfully) navigate('/my-applications'); }}>
        <DialogContent className="max-w-lg">
          {appliedSuccessfully ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-5 h-5" />Application Submitted!
                </DialogTitle>
                <DialogDescription>Your application to {job?.title} at {job?.company_name} has been sent.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-start gap-3">
                    <Bell className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-blue-800 text-sm">Want more jobs like this?</p>
                      <p className="text-xs text-blue-700 mt-0.5">
                        Set up a job alert for <strong>{job?.title}</strong> roles and get notified when new matches are posted.
                      </p>
                      <Button size="sm" className="mt-2 bg-blue-600 hover:bg-blue-700 text-xs h-7"
                        onClick={() => { setShowApplyModal(false); navigate(`/alerts`); }}>
                        <Bell className="w-3.5 h-3.5 mr-1.5" />Create Job Alert
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowApplyModal(false)}>Stay Here</Button>
                <Button className="bg-[#003366]" onClick={() => navigate('/my-applications')}>Track Application</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Apply for {job?.title}</DialogTitle>
                <DialogDescription>at {job?.company_name}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Cover Letter (Optional)</label>
                    <Button variant="ghost" size="sm" onClick={generateCoverLetter}
                      disabled={generatingCoverLetter} className="text-purple-600 hover:text-purple-700"
                      data-testid="generate-cover-letter">
                      {generatingCoverLetter ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      AI Generate
                    </Button>
                  </div>
                  <Textarea placeholder="Write a cover letter or use AI to generate one..."
                    value={coverLetter} onChange={e => setCoverLetter(e.target.value)}
                    rows={8} data-testid="cover-letter-input" />
                </div>
                {user && (
                  <div className="bg-secondary rounded-lg p-4 text-sm">
                    <p className="font-medium mb-2">Applying as:</p>
                    <p>{user.full_name} ({user.email})</p>
                    {user.cv_url && (
                      <p className="text-muted-foreground mt-1 flex items-center gap-1">
                        <FileText className="w-4 h-4" />CV attached
                      </p>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowApplyModal(false)}>Cancel</Button>
                <Button onClick={submitApplication} disabled={applying}
                  className="bg-[#E11D48] hover:bg-[#E11D48]/90" data-testid="submit-application">
                  {applying && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Submit Application
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
